interface FileUploaderProps {
  label: string
  error?: string
  onFileSelect: (file: File | null) => void
  onError?: (message: string) => void
  selectedFileName?: string
  uploadProgress?: number
  config?: {
    maxMB: number
    maxSize: number
    allowedMimeTypes: string[]
    allowedExtensions: string[]
  }
}

export const CSV_FILE_UPLOAD_CONFIG = {
  maxMB: 10,
  maxSize: 10 * 1024 * 1024,
  allowedMimeTypes: [
    'text/csv',
    'application/vnd.ms-excel',
    'text/comma-separated-values',
  ],
  allowedExtensions: ['.csv'],
}

const CsvFileUploader = ({
  label,
  error,
  onFileSelect,
  selectedFileName,
  uploadProgress,
  config = CSV_FILE_UPLOAD_CONFIG,
}: FileUploaderProps) => {
  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault()
    onFileSelect(null)
  }

  return (
    <div className="flex flex-col gap-2.5">
      <label className="ml-0.5 text-sm font-medium text-gray-700">
        {label}
      </label>

      <div
        className={`group relative flex flex-col items-center justify-center rounded-2xl border-2 border-dashed transition-all duration-200 ease-in-out
        ${
          error
            ? 'border-red-300 bg-red-50/50'
            : 'border-gray-300 bg-gray-50/50 hover:border-blue-400 hover:bg-white hover:shadow-md'
        } ${selectedFileName ? 'py-6' : 'py-10'}`}
      >
        {/* Hidden Input */}
        <input
          key={selectedFileName || 'reset'}
          type="file"
          className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
          accept={config.allowedExtensions.join(',')}
          onChange={(e) => onFileSelect(e.target.files?.[0] || null)}
        />

        {/* Icon & Content */}
        <div className="flex flex-col items-center text-center">
          <div
            className={`mb-3 flex h-12 w-12 items-center justify-center rounded-full transition-colors 
            ${
              error
                ? 'bg-red-100 text-red-600'
                : 'bg-blue-50 text-blue-600 group-hover:bg-blue-100'
            }`}
          >
            {selectedFileName ? (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
            ) : (
              <svg
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            )}
          </div>

          <div className="px-4">
            <p className="text-sm font-semibold text-gray-900">
              {selectedFileName ? (
                <span className="block max-w-[240px] truncate">
                  {selectedFileName}
                </span>
              ) : (
                'Click to upload or drag & drop'
              )}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              {config.allowedExtensions.join(', ').toUpperCase()} files up to{' '}
              {config.maxSize / (1024 * 1024)}MB
            </p>
          </div>
        </div>

        {/* Remove Button */}
        {selectedFileName && (
          <button
            type="button"
            onClick={handleRemove}
            className="absolute right-4 top-4 z-20 flex h-7 w-7 items-center justify-center rounded-full border border-gray-100 bg-white text-gray-400 shadow-sm transition-all hover:border-red-100 hover:bg-red-50 hover:text-red-500"
            title="Remove file"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {selectedFileName && uploadProgress !== undefined && (
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-gray-500">
            <span>Uploading...</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-full rounded-full bg-blue-500 transition-all duration-200"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {error && (
        <span className="text-xs font-medium italic text-red-600">{error}</span>
      )}
    </div>
  )
}

export default CsvFileUploader
